import { Component, Input, OnChanges } from '@angular/core';
import { ChartTypeOption } from '../chart-type-toggle/chart-type-toggle.component';
import { ReportColumn } from '../data-table/data-table.component';
import { CHART_COLORS, formatChartValue, prepareChartData, prepareStatsData } from '../../utils/chart-theme';

const COLOR_MAP: any = {
  blue: CHART_COLORS[2],
  primary: CHART_COLORS[0],
  success: CHART_COLORS[1],
  warning: CHART_COLORS[3],
  info: CHART_COLORS[4],
  danger: CHART_COLORS[5]
};

const GRID_STROKE = '#dee2e6';
const TICK_MUTED = '#98a6ad';
const TICK_BODY = '#323a46';

const BAR_H_TYPES: ChartTypeOption[] = [
  { id: 'bar-h', label: 'Bar' },
  { id: 'bar-v', label: 'Column' },
  { id: 'line', label: 'Line' },
  { id: 'table', label: 'Table' }
];

const BAR_V_TYPES: ChartTypeOption[] = [
  { id: 'bar-v', label: 'Bar' },
  { id: 'line', label: 'Line' },
  { id: 'bar-h', label: 'Horizontal' },
  { id: 'table', label: 'Table' }
];

/**
 * Horizontal/vertical/line category chart. Set `orientation="horizontal"` to
 * mirror React's BarChart (default bar-h), or "vertical" to mirror
 * StatBarChart/VerticalBarChart (default bar-v, optionally keeping zeros).
 */
@Component({
  selector: 'app-br-bar-chart',
  templateUrl: './bar-chart.component.html',
  host: { '[attr.title]': 'null' }
})
export class BrBarChartComponent implements OnChanges {
  @Input() chartTitle: string;
  /** Short line under the title explaining how the chart values are calculated. */
  @Input() chartDescription: string;
  @Input() items: any[] = [];
  @Input() color: string;
  @Input() colorClass: string;
  @Input() valueFormatter: (v: number) => string;
  @Input() maxItems = 8;
  @Input() emptyLabel = 'No data';
  @Input() orientation: 'horizontal' | 'vertical' = 'horizontal';
  @Input() includeZero = false;

  chartType: string;
  chartTypeOptions: ChartTypeOption[];
  data: any[] = [];
  resolvedColor: string;

  chartLabels: string[] = [];
  chartDatasets: any[] = [];
  chartOptions: any = {};
  ngChartType = 'horizontalBar';
  tableColumns: ReportColumn[] = [];

  ngOnChanges() {
    this.chartTypeOptions = this.orientation === 'horizontal' ? BAR_H_TYPES : BAR_V_TYPES;
    if (!this.chartType) {
      this.chartType = 'table';
    }
    this.resolvedColor = this.color || (this.colorClass && COLOR_MAP[this.colorClass]) || CHART_COLORS[0];
    this.data = this.includeZero
      ? prepareStatsData(this.items, 'value', 'label', this.maxItems)
      : prepareChartData(this.items, 'value', 'label', this.maxItems);
    this.tableColumns = [
      { key: 'label', header: 'Category' },
      { key: 'value', header: 'Value', value: true, render: (row) => formatChartValue(row.value, this.valueFormatter) }
    ];
    this.rebuildChart();
  }

  onChartTypeChange(type: string) {
    this.chartType = type;
    this.rebuildChart();
  }

  private rebuildChart() {
    this.chartLabels = this.data.map((d) => d.label);
    const values = this.data.map((d) => d.value);
    this.chartDatasets = [{ data: values, backgroundColor: this.resolvedColor }];

    const formatter = this.valueFormatter;
    const tooltipCallbacks = {
      title: () => null,
      label: (tooltipItem: any) => {
        const category = this.chartType === 'bar-h' ? tooltipItem.yLabel : tooltipItem.xLabel;
        const raw = this.chartType === 'bar-h' ? tooltipItem.xLabel : tooltipItem.yLabel;
        const formatted = formatChartValue(Number(raw), formatter);
        return category ? `${category}: ${formatted}` : formatted;
      }
    };

    if (this.chartType === 'bar-h') {
      this.ngChartType = 'horizontalBar';
      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: { callbacks: tooltipCallbacks },
        scales: {
          xAxes: [{ gridLines: { color: GRID_STROKE, drawBorder: false }, ticks: { fontColor: TICK_MUTED, fontSize: 12, beginAtZero: true } }],
          yAxes: [{ gridLines: { display: false, drawBorder: false }, ticks: { fontColor: TICK_BODY, fontSize: 12 } }]
        }
      };
    } else if (this.chartType === 'line') {
      this.ngChartType = 'line';
      this.chartDatasets = [{
        data: values,
        borderColor: this.resolvedColor,
        backgroundColor: 'transparent',
        pointBackgroundColor: this.resolvedColor,
        fill: false,
        tension: 0.3
      }];
      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: { callbacks: tooltipCallbacks },
        scales: {
          xAxes: [{ gridLines: { display: false, drawBorder: false }, ticks: { fontColor: TICK_BODY, fontSize: 11, minRotation: 20, maxRotation: 45 } }],
          yAxes: [{ gridLines: { color: GRID_STROKE, drawBorder: false }, ticks: { fontColor: TICK_MUTED, fontSize: 12, beginAtZero: true } }]
        }
      };
    } else {
      this.ngChartType = 'bar';
      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: { callbacks: tooltipCallbacks },
        scales: {
          xAxes: [{ gridLines: { display: false, drawBorder: false }, ticks: { fontColor: TICK_BODY, fontSize: 11, minRotation: 20, maxRotation: 45 } }],
          yAxes: [{ gridLines: { color: GRID_STROKE, drawBorder: false }, ticks: { fontColor: TICK_MUTED, fontSize: 12, beginAtZero: true } }]
        }
      };
    }
  }

  get chartHeight(): number {
    if (this.chartType === 'bar-h') {
      return Math.max(220, this.data.length * 42 + 40);
    }
    return 260;
  }
}
